import { WatchQueryFetchPolicy } from '@apollo/client';
import { OfferFragment } from '@occc/graphql';
import { Feature } from '@occc/utils-access-control';
import { getOfferComponentType } from '@occc-app/offerHelpers/utils';
import { OfferType } from '@occc-app/offerValidationService/types';
import { routeCreator as awardRouteCreator } from '@occc-app/routes/AwardCatalog/utils';
import { createRouteCreator as createCampaignComponentRouteCreator } from '@occc-app/routes/CampaignComponents/utils';
import { CampaignComponentTabs } from '@occc-app/routes/CampaignComponents/components/ConfigurationLayout';
import {
  campaignManagementGameRouteCreator,
  programManagementGameRouteCreator,
} from '@occc-app/routes/Game/utils';
import { routeCreator as loyaltyEventRouteCreator } from '@occc-app/routes/LoyaltyEvent/utils';
import { createRouteCreator as createProgramManagementRouteCreator } from '@occc-app/routes/ProgramManagement/utils';
import { routeCreator as storedValueRouteCreator } from '@occc/feat-stored-value/features/stored-value.utils';

const campaignOfferEditFeatures = [
  Feature.CAMPAIGN_COMPONENT_EDIT,
  Feature.CAMPAIGN_COMPONENT_EDIT_VIEW_ONLY,
] as const;

const programManagementOfferEditFeaturesByType: Partial<Record<OfferType, readonly Feature[]>> = {
  [OfferType.LoyaltyEvent]: [Feature.LOYALTY_EVENT_EDIT, Feature.LOYALTY_EVENT_EDIT_VIEW_ONLY],
  [OfferType.VoucheredGame]: [Feature.GAME_EDIT, Feature.GAME_EDIT_VIEW_ONLY],
  [OfferType.FreeGame]: [Feature.GAME_EDIT, Feature.GAME_EDIT_VIEW_ONLY],
  [OfferType.Award]: [Feature.AWARD_EDIT, Feature.AWARD_EDIT_VIEW_ONLY],
  [OfferType.StoredValue]: [Feature.OFFER_EDIT],
};

const programManagementOnlyOfferTypes = new Set<OfferType>([
  OfferType.LoyaltyEvent,
  OfferType.Award,
  OfferType.VoucheredGame,
  OfferType.StoredValue,
]);

export const offerEditRedirectRouteCreator = {
  edit: (offerId: string, tab?: CampaignComponentTabs) =>
    tab ? `/edit/${offerId}/${tab}` : `/edit/${offerId}`,
};

export type OfferNavigationState = {
  fetchPolicy?: Extract<WatchQueryFetchPolicy, 'cache-first' | 'no-cache'>;
};

export const offerEditRedirectNavigationState: OfferNavigationState = {
  fetchPolicy: 'cache-first',
};

export const getOfferFetchPolicyFromNavigationState = (
  state: unknown,
): Extract<WatchQueryFetchPolicy, 'cache-first' | 'no-cache'> => {
  const fetchPolicy = (state as OfferNavigationState | null)?.fetchPolicy;

  return fetchPolicy === 'cache-first' ? 'cache-first' : 'no-cache';
};

export const getOfferEditRedirectFeatures = (offer: OfferFragment): readonly Feature[] | null => {
  const type = getOfferComponentType(offer);

  if (!type) {
    return null;
  }

  if (programManagementOnlyOfferTypes.has(type)) {
    return programManagementOfferEditFeaturesByType[type] ?? null;
  }

  if (type === OfferType.FreeGame) {
    return offer.campaign?.uuid
      ? campaignOfferEditFeatures
      : programManagementOfferEditFeaturesByType[OfferType.FreeGame] ?? null;
  }

  if (offer.campaign?.uuid) {
    return campaignOfferEditFeatures;
  }

  return programManagementOfferEditFeaturesByType[type] ?? null;
};

export const isUserPermittedToEditOffer = (
  offer: OfferFragment,
  isUserPermittedTo: (feature: Feature) => boolean,
): boolean => {
  const features = getOfferEditRedirectFeatures(offer);

  return features !== null && features.some((feature) => isUserPermittedTo(feature));
};

const resolveProgramManagementEditUrl = (
  type: OfferType,
  offerId: string,
  tab: CampaignComponentTabs,
): string => {
  switch (type) {
    case OfferType.LoyaltyEvent:
      return loyaltyEventRouteCreator.updateWithTab(offerId, tab);
    case OfferType.Award:
      return awardRouteCreator.update(offerId);
    case OfferType.StoredValue:
      return storedValueRouteCreator.updateByOffer(offerId);
    case OfferType.VoucheredGame:
    case OfferType.FreeGame:
      return programManagementGameRouteCreator.updateWithTab(offerId, tab);
    default:
      return createProgramManagementRouteCreator(type).updateWithTab(offerId, tab);
  }
};

export const resolveOfferEditUrl = (
  offer: OfferFragment,
  tab: CampaignComponentTabs = CampaignComponentTabs.Configure,
): string | null => {
  const type = getOfferComponentType(offer);

  if (!type) {
    return null;
  }

  if (programManagementOnlyOfferTypes.has(type)) {
    return resolveProgramManagementEditUrl(type, offer.id, tab);
  }

  if (type === OfferType.FreeGame && offer.campaign?.uuid) {
    return campaignManagementGameRouteCreator.updateWithTab(offer.campaign.uuid, offer.id, tab);
  }

  if (type === OfferType.FreeGame) {
    return programManagementGameRouteCreator.updateWithTab(offer.id, tab);
  }

  if (offer.campaign?.uuid) {
    return createCampaignComponentRouteCreator(type).updateWithTab(
      offer.campaign.uuid,
      offer.id,
      tab,
    );
  }

  return resolveProgramManagementEditUrl(type, offer.id, tab);
};
